import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import usePrevious from '~/hooks/usePrevious';
import useSimilarityAlignment from '~/hooks/useSimilarityAlignment';
import prepareComparisonDocs from '~/utils/prepareComparisonDocs';
import { useDocumentComparison } from '../document_comparsion';
const ComparisonGridContext = createContext({});
const ComparisonGridProvider = (props) => {
    const { commentId } = useParams();
    const { comparisonDocuments, anchorDocId } = useDocumentComparison();
    const prevAnchorDocId = usePrevious(anchorDocId);
    const [isSimilarityEnabled, setIsSimilarityEnabled, canUseSimilarity] = useSimilarityAlignment();
    const prevIsSimilarityEnabled = usePrevious(isSimilarityEnabled);
    const [anchorDoc, setAnchorDoc] = useState({});
    const [comparatorDocs, setComparatorDocs] = useState([]);
    const [tagsColumn, setTagsColumn] = useState(new Map());
    const [tagsColumnNames, setTagColumnNames] = useState([]);
    const [categories, setCategories] = useState([]);
    const [descriptors, setDescriptors] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedDescriptors, setSelectedDescriptors] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
    const [anchorDocCellSelection, setAnchorDocCellSelection] = useState(new Map());
    const [areDiffsVisible, setAreDiffsVisible] = useState(false);
    const [areCommentsVisible, setAreCommentsVisible] = useState(!!(commentId));
    useEffect(() => {
        const anchorChanged = anchorDocId !== prevAnchorDocId;
        const similarityChanged = isSimilarityEnabled !== prevIsSimilarityEnabled;
        if (comparisonDocuments.length &&
            anchorDocId !== undefined &&
            (anchorChanged || similarityChanged)) {
            const preparedInfo = prepareComparisonDocs(anchorDocId, comparisonDocuments, isSimilarityEnabled);
            setCategories(preparedInfo.categories);
            setDescriptors(preparedInfo.descriptors);
            setTagsColumn(preparedInfo.tagsColumn);
            const anchorDocument = preparedInfo.documents.find(doc => doc.documentComparisonFileId === anchorDocId);
            if (anchorDocument)
                setAnchorDoc(anchorDocument);
            setComparatorDocs(preparedInfo.documents.filter(doc => doc.documentComparisonFileId !== anchorDocId));
        }
    }, [comparisonDocuments, anchorDocId, isSimilarityEnabled]);
    useEffect(() => {
        const filteredTagsColumn = [];
        for (const [k, v] of tagsColumn) {
            const matchCat = !selectedCategories.length || selectedCategories.includes(v.category);
            const matchDes = !selectedDescriptors.length || selectedDescriptors.includes(v.descriptor);
            if (matchCat && matchDes)
                filteredTagsColumn.push(k);
        }
        setTagColumnNames(filteredTagsColumn);
    }, [tagsColumn, selectedCategories, selectedDescriptors]);
    const value = {
        anchorDoc,
        setAnchorDoc,
        comparatorDocs,
        setComparatorDocs,
        tagsColumnNames,
        categories,
        descriptors,
        selectedCategories,
        setSelectedCategories,
        selectedDescriptors,
        setSelectedDescriptors,
        selectedRowIndex,
        setSelectedRowIndex,
        anchorDocCellSelection,
        setAnchorDocCellSelection,
        areDiffsVisible,
        setAreDiffsVisible,
        areCommentsVisible,
        setAreCommentsVisible,
        isSimilarityEnabled,
        setIsSimilarityEnabled,
        canUseSimilarity,
    };
    return React.createElement(ComparisonGridContext.Provider, Object.assign({ value: value }, props));
};
const useComparisonGrid = () => {
    const ctx = useContext(ComparisonGridContext);
    if (ctx === undefined) {
        throw new Error('ComparisonGridContext can only be used under the ComparisonGridProvider');
    }
    return ctx;
};
export { useComparisonGrid, ComparisonGridProvider, };
